/**
 * Calculates the relative luminance of a color according to WCAG standards
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Relative luminance value (0-1)
 */
const getRelativeLuminance = (r: number, g: number, b: number) => {
  const [rs, gs, bs] = [r, g, b].map((val) => {
    const normalized = val / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculates the contrast ratio between two colors according to WCAG standards
 * @param luminance1 Luminance of the first color
 * @param luminance2 Luminance of the second color
 * @returns Contrast ratio (1-21)
 */
const getContrastRatio = (luminance1: number, luminance2: number) => {
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Converts a hex color code to RGB values
 * @param hex Hex color code (with or without #)
 * @returns RGB values as [r, g, b] or null if invalid
 */
const hexToRgb = (hex: string) => {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Handle 3-digit hex codes
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    // Check if any value is NaN (invalid hex)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return null;
    }
    return [r, g, b];
  }

  // Handle 6-digit hex codes
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    // Check if any value is NaN (invalid hex)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
      return null;
    }
    return [r, g, b];
  }

  return null;
};

/**
 * Determines whether white or black text would provide better contrast
 * against a given background color.
 *
 * Uses WCAG contrast ratio calculations to ensure accessibility.
 */
export function getContrastTextColor(backgroundColorHex: string) {
  const rgb = hexToRgb(backgroundColorHex);

  if (!rgb) {
    throw new Error(
      `Invalid hex color code: ${backgroundColorHex}. Expected format: #RRGGBB or #RGB`,
    );
  }

  const [r, g, b] = rgb;
  const backgroundLuminance = getRelativeLuminance(r, g, b);

  // White (#FFFFFF) luminance is 1.0
  const whiteLuminance = 1.0;

  const contrastWithWhite = getContrastRatio(
    backgroundLuminance,
    whiteLuminance,
  );

  // Prefer white text when it provides sufficient contrast (WCAG AA large text: 3:1).
  // Only fall back to black for light backgrounds where white would be hard to read.
  const MINIMUM_CONTRAST_RATIO = 3;
  return contrastWithWhite >= MINIMUM_CONTRAST_RATIO ? 'white' : 'black';
}
